/**
 * Shared keyboard controller for slide-up search dialogs that use mot-input +
 * a custom <ul role="listbox"> (address / line / station autocomplete).
 *
 * Tab order: input → clear button → first option (DOM focus) → Tab commits.
 * Shift+Tab walks back: option → clear → input.
 * While typing, arrows on the input use aria-activedescendant (virtual focus).
 */
export function createSlideUpListboxId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
}

export class SlideUpComboboxListKeyboard {
  activeOptionIndex = -1;
  listboxDomFocus = false;

  constructor(
    public readonly listboxId: string,
    private readonly getCurrentResults: () => unknown[],
    private readonly getIsListboxExpanded: () => boolean,
    private readonly getSearchInputHost: () => HTMLElement | undefined,
    private readonly commitAtIndex: (index: number) => void
  ) {}

  getOptionId(index: number): string {
    return `${this.listboxId}-opt-${index}`;
  }

  get activeOptionId(): string | null {
    if (this.listboxDomFocus) {
      return null;
    }
    return this.activeOptionIndex >= 0
      ? this.getOptionId(this.activeOptionIndex)
      : null;
  }

  resetHighlight(): void {
    this.activeOptionIndex = -1;
    this.listboxDomFocus = false;
  }

  onComboboxKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;

    if (
      event.key === 'Tab' &&
      this.getIsListboxExpanded() &&
      target.tagName === 'BUTTON'
    ) {
      if (event.shiftKey) {
        event.preventDefault();
        this.focusSearchInput();
        return;
      }
      event.preventDefault();
      this.focusOptionAt(0);
      return;
    }

    if (target.tagName !== 'INPUT') {
      return;
    }

    if (!this.getIsListboxExpanded()) {
      return;
    }

    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter'].includes(event.key)) {
      this.listboxDomFocus = false;
    }
    this.handleOptionNavigationKeydown(event, false);
  }

  onListboxKeydown(event: KeyboardEvent): void {
    if (!this.getIsListboxExpanded()) {
      return;
    }
    this.handleOptionNavigationKeydown(event, true);
  }

  onListboxContainerFocus(event: FocusEvent): void {
    if (!this.getIsListboxExpanded() || this.getCurrentResults().length === 0) {
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    const related = event.relatedTarget as HTMLElement | null;
    if (
      related?.tagName === 'BUTTON' ||
      related?.getAttribute('role') === 'option'
    ) {
      event.preventDefault();
      this.focusClearButton();
      return;
    }
    event.preventDefault();
    this.focusOptionAt(0);
  }

  onOptionFocus(index: number): void {
    this.listboxDomFocus = true;
    this.activeOptionIndex = index;
  }

  private handleOptionNavigationKeydown(
    event: KeyboardEvent,
    moveDomFocus: boolean
  ): void {
    const total = this.getCurrentResults().length;
    if (total === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next =
          this.activeOptionIndex < total - 1 ? this.activeOptionIndex + 1 : 0;
        if (moveDomFocus) {
          this.focusOptionAt(next);
        } else {
          this.activeOptionIndex = next;
        }
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const next =
          this.activeOptionIndex > 0 ? this.activeOptionIndex - 1 : total - 1;
        if (moveDomFocus) {
          this.focusOptionAt(next);
        } else {
          this.activeOptionIndex = next;
        }
        return;
      }
      case 'Home': {
        event.preventDefault();
        if (moveDomFocus) {
          this.focusOptionAt(0);
        } else {
          this.activeOptionIndex = 0;
        }
        return;
      }
      case 'End': {
        event.preventDefault();
        const last = total - 1;
        if (moveDomFocus) {
          this.focusOptionAt(last);
        } else {
          this.activeOptionIndex = last;
        }
        return;
      }
      case 'Enter':
      case 'Tab': {
        if (event.key === 'Tab' && event.shiftKey) {
          if (!moveDomFocus) {
            return;
          }
          event.preventDefault();
          if (this.activeOptionIndex <= 0) {
            this.focusClearButton();
          } else {
            this.focusOptionAt(this.activeOptionIndex - 1);
          }
          return;
        }
        if (this.activeOptionIndex >= 0) {
          event.preventDefault();
          this.commitAtIndex(this.activeOptionIndex);
        }
        return;
      }
      case 'Escape': {
        if (this.activeOptionIndex >= 0 || this.listboxDomFocus) {
          event.preventDefault();
          this.resetHighlight();
        }
        return;
      }
    }
  }

  private focusOptionAt(index: number): void {
    if (index < 0 || index >= this.getCurrentResults().length) {
      return;
    }
    this.listboxDomFocus = true;
    this.activeOptionIndex = index;
    const option = document.getElementById(this.getOptionId(index));
    option?.focus({ preventScroll: true });
    option?.scrollIntoView({ block: 'nearest' });
  }

  private focusClearButton(): void {
    this.listboxDomFocus = false;
    this.activeOptionIndex = -1;
    const button = this.getSearchInputHost()?.querySelector('button');
    (button as HTMLButtonElement | null)?.focus({ preventScroll: true });
  }

  private focusSearchInput(): void {
    this.listboxDomFocus = false;
    this.activeOptionIndex = -1;
    const input = this.getSearchInputHost()?.querySelector('input');
    (input as HTMLInputElement | null)?.focus({ preventScroll: true });
  }
}
